import { Component, computed, inject, input, output, signal } from '@angular/core';
import { ColDef, GridApi, GridReadyEvent, ValueSetterParams } from 'ag-grid-community';
import { AgGridAngular } from 'ag-grid-angular';
import {
  TechnicianStockLevel,
  TechnicianStockLevelDraft,
  TechnicianStockLevelSelection,
  TechnicianStockLevelService,
} from '../../_services/technician-stock-level.service';
import { AgGridThemeService } from '../../_services/ag-grid-theme.service';
import { ModalService } from '../../_services';
import { germanStateLabel } from '../../_helpers/german-states';
import { TechnicianStockLevelDialogComponent } from './technician-stock-level-dialog.component';

/** StockRow im Grid — saved (mit id) oder erst vorgemerkt (ohne id). */
interface StockRow extends TechnicianStockLevelDraft {
  id?: number;
  label: string;
  addressState?: string | null;
}

/**
 * Mindest- und Sollbestände als Tabelle, mit Dialog zum Anlegen.
 *
 * Dieselbe Komponente bedient beide Seiten: auf der Technikerseite zeigt sie
 * dessen Artikelvorlagen, auf der Vorlagenseite die Techniker, die den Artikel
 * führen. Das ist keine Doppelung — es sind zwei Sichten auf dieselben Zeilen.
 *
 * Ohne Anker-ID (Anlegen-Dialog eines neuen Technikers bzw. einer neuen
 * Vorlage) hält die Komponente die Zeilen nur lokal und meldet sie über
 * `draftsChange` nach oben; saved werden sie erst mit dem Datensatz.
 */
@Component({
  selector: 'hawk-technician-stock-levels-grid',
  standalone: true,
  imports: [AgGridAngular],
  templateUrl: './technician-stock-levels-grid.component.html',
})
export class TechnicianStockLevelsGridComponent {
  private service = inject(TechnicianStockLevelService);
  private modal = inject(ModalService);
  protected hawkTheme = inject(AgGridThemeService).getTheme();

  public mode = input.required<'technician' | 'template'>();
  /** Anker: gesetzt beim Bearbeiten, leer beim Anlegen. */
  public userId = input<number | null>(null);
  public articleTemplateId = input<number | null>(null);
  /** Bundesland des Technikers — Vorauswahl der Durchschnittswerte im Dialog. */
  public addressState = input<string | null>(null);
  /** Kunde bzw. Projekte als Vorfilter der Auswahl im Dialog. */
  public customerId = input<number | null>(null);
  public projectIds = input<number[]>([]);
  /** Rohwerte der Vorlage fuer die Projekt-Vorauswahl, solange sie ungespeichert ist. */
  public comment2 = input<string | null>(null);
  public comment3 = input<string | null>(null);

  /** Nur im Anlegen-Fall relevant: die noch nicht gespeicherten Zeilen. */
  public draftsChange = output<TechnicianStockLevelDraft[]>();
  /** Anzahl der Zeilen — fuer Zaehler ausserhalb der Komponente (z.B. im Reiter). */
  public countChange = output<number>();

  protected rows = signal<StockRow[]>([]);
  protected loading = signal(false);
  protected fehler = signal<string | null>(null);
  protected selection = signal<StockRow[]>([]);

  private gridApi?: GridApi<StockRow>;

  protected istTechnikerModus = computed(() => this.mode() === 'technician');
  protected ankerVorhanden = computed(() =>
    this.istTechnikerModus() ? this.userId() != null : this.articleTemplateId() != null
  );

  /** IDs der Gegenstücke, die im Dialog nicht noch einmal wählbar sein dürfen. */
  protected vergebeneIds = computed(() =>
    this.rows().map(z => (this.istTechnikerModus() ? z.article_template_id : z.user_id))
  );

  protected colDefs = computed<ColDef<StockRow>[]>(() => {
    const spalten: ColDef<StockRow>[] = [
      {
        headerName: this.istTechnikerModus() ? 'Artikelvorlage' : 'Techniker',
        field: 'label', flex: 2, minWidth: 220,
        checkboxSelection: true, headerCheckboxSelection: true,
      },
    ];

    if (!this.istTechnikerModus()) {
      spalten.push({
        headerName: 'Bundesland', flex: 1, minWidth: 150,
        valueGetter: p => germanStateLabel(p.data?.addressState),
      });
    }

    spalten.push(
      // Das Stift-Zeichen im Kopf und die helle Zellfarbe machen sichtbar, dass
      // die Spalte editierbar ist — ohne Hinweis wirkt die Tabelle nur lesend.
      {
        headerName: '✏️ Mindestbestand', field: 'min_quantity', width: 180, editable: true,
        cellClass: 'bg-primary/5 font-semibold',
        valueSetter: p => this.setzeWert(p, 'min_quantity'),
      },
      {
        headerName: '✏️ Sollbestand', field: 'target_quantity', width: 160, editable: true,
        cellClass: 'bg-primary/5 font-semibold',
        valueSetter: p => this.setzeWert(p, 'target_quantity'),
      },
    );

    return spalten;
  });

  protected defaultColDef: ColDef = { sortable: true, resizable: true, filter: true };

  async ngOnInit(): Promise<void> {
    await this.laden();
  }

  protected onGridReady(event: GridReadyEvent<StockRow>): void {
    this.gridApi = event.api;
  }

  protected onSelectionChanged(): void {
    this.selection.set(this.gridApi?.getSelectedRows() ?? []);
  }

  private async laden(): Promise<void> {
    if (!this.ankerVorhanden()) return;

    this.loading.set(true);
    try {
      const daten = await this.service.list({
        userId: this.istTechnikerModus() ? (this.userId() ?? undefined) : undefined,
        articleTemplateId: this.istTechnikerModus() ? undefined : (this.articleTemplateId() ?? undefined),
      });
      this.rows.set(daten.map(entry => this.zuZeile(entry)));
      this.countChange.emit(this.rows().length);
    } catch {
      this.fehler.set('Die Bestände konnten nicht geladen werden.');
    } finally {
      this.loading.set(false);
    }
  }

  private zuZeile(entry: TechnicianStockLevel): StockRow {
    return {
      id: entry.id,
      user_id: entry.user_id,
      article_template_id: entry.article_template_id,
      min_quantity: entry.min_quantity,
      target_quantity: entry.target_quantity,
      addressState: entry.address_state,
      label: this.mode() === 'technician' ? entry.article_template_description : entry.user_name,
    };
  }

  /**
   * Inline-Änderung: erst gegen dieselben Regeln prüfen wie im Dialog, dann
   * speichern. Eine ungültige Eingabe wird abgelehnt statt stillschweigend
   * übernommen — sonst stünde im Grid ein Wert, den das Backend nie bekommen hat.
   */
  private setzeWert(params: ValueSetterParams<StockRow>, feld: 'min_quantity' | 'target_quantity'): boolean {
    // Ganze Stück: Die Spalte ist ganzzahlig, eine Nachkommastelle würde erst
    // das Backend ablehnen. Lieber hier abweisen als eine Zahl anzeigen, die nie
    // saved wurde.
    const zahl = Number(params.newValue);
    if (!Number.isInteger(zahl) || zahl <= 0) {
      this.fehler.set('Bitte eine ganze Stückzahl größer 0 eintragen.');
      return false;
    }

    const row = params.data;
    const min = feld === 'min_quantity' ? zahl : row.min_quantity;
    const target = feld === 'target_quantity' ? zahl : row.target_quantity;
    if (target <= min) {
      this.fehler.set('Der Sollbestand muss über dem Mindestbestand liegen.');
      return false;
    }

    this.fehler.set(null);
    row[feld] = zahl;
    this.rows.update(current => [...current]);
    void this.speichereZeile(row);
    this.meldeDrafts();
    return true;
  }

  private async speichereZeile(row: StockRow): Promise<void> {
    if (row.id == null) return;

    try {
      await this.service.update(row.id, row.min_quantity, row.target_quantity);
    } catch (error) {
      this.fehler.set(error instanceof Error ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  protected async hinzufuegen(): Promise<void> {
    const ref = this.modal.open<TechnicianStockLevelDialogComponent, TechnicianStockLevelSelection[]>(
      TechnicianStockLevelDialogComponent,
      {
        maxWidth: '960px',
        inputs: {
          mode: this.mode(),
          userId: this.userId(),
          articleTemplateId: this.articleTemplateId(),
          addressState: this.addressState(),
          customerId: this.customerId(),
          projectIds: this.projectIds(),
          comment2: this.comment2(),
          comment3: this.comment3(),
          pendingIds: this.vergebeneIds(),
        },
      }
    );

    const selection = await new Promise<TechnicianStockLevelSelection[] | undefined>(resolve => {
      ref.closed.subscribe(wert => resolve(wert));
    });
    if (!selection || selection.length === 0) return;

    await this.applySelection(selection);
  }

  private async applySelection(selection: TechnicianStockLevelSelection[]): Promise<void> {
    // Ohne Anker (Anlegen) bleiben die Zeilen lokal; das Formular schickt sie
    // erst mit dem neuen Datensatz ans Backend. Die Beschriftung kommt aus dem
    // Dialog mit — die Gegenseite steht noch nicht in der Datenbank.
    if (!this.ankerVorhanden()) {
      this.rows.update(current => [...current, ...selection.map(e =>
        ({ ...e.draft, label: e.label, addressState: e.addressState }))]);
      this.meldeDrafts();
      return;
    }

    try {
      const saved = await this.service.createMany(selection.map(e => e.draft));
      this.rows.update(current => [...current, ...saved.map(e => this.zuZeile(e))]);
      this.countChange.emit(this.rows().length);
      this.fehler.set(null);
    } catch (error) {
      this.fehler.set(error instanceof Error ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  protected async entfernen(): Promise<void> {
    const toDelete = this.selection();
    if (toDelete.length === 0) return;

    for (const row of toDelete) {
      if (row.id != null) {
        try {
          await this.service.remove(row.id);
        } catch {
          this.fehler.set('Mindestens eine StockRow konnte nicht entfernt werden.');
        }
      }
    }

    const ids = new Set(toDelete);
    this.rows.update(current => current.filter(z => !ids.has(z)));
    this.selection.set([]);
    this.meldeDrafts();
  }

  private meldeDrafts(): void {
    this.countChange.emit(this.rows().length);
    this.draftsChange.emit(this.rows().map(({ user_id, article_template_id, min_quantity, target_quantity }) =>
      ({ user_id, article_template_id, min_quantity, target_quantity })));
  }
}
